"use client";
import React, { useState } from 'react';
import Head from 'next/head';
import ProteinPageLayout from '@/app/components/Layout/ProteinPageLayout';
import Link from "next/link";

interface BlastHit {
  // query: string;
  subject: string;
  identity: number;
  alignmentLength: number;
  mismatches: number;
  gapOpens: number;
  queryStart: number;
  queryEnd: number;
  subjectStart: number;
  subjectEnd: number;
  eValue: string;
  bitScore: number;
}

const SearchBlastPage: React.FC = () => {
  const [searchBy, setSearchBy] = useState<string>('GCF_003047695.1_ASM304769v1_translated_cds.faa');
  const [textareaValue, setTextareaValue] = useState<string>('');
  const [warning, setWarning] = useState<string>('');
  const [blastResult, setBlastResult] = useState<string | null>(null);
  const [parsedResults, setParsedResults] = useState<BlastHit[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [cachedQueries, setCachedQueries] = useState<{[key: string]: string}>({});
  const [selectedCachedQuery, setSelectedCachedQuery] = useState<string>('');

  React.useEffect(() => {
    const cached = localStorage.getItem('blastCachedQueries');
    if (cached) {
      try {
        setCachedQueries(JSON.parse(cached));
      } catch (error) {
        console.error('Error loading cached queries:', error);
      }
    }
  }, []);

  const saveQueryToCache = (name: string, sequence: string) => {
    const newCache = { ...cachedQueries, [name]: sequence };
    setCachedQueries(newCache);
    localStorage.setItem('blastCachedQueries', JSON.stringify(newCache));
  };

  const deleteQueryFromCache = (name: string) => {
    const newCache = { ...cachedQueries };
    delete newCache[name];
    setCachedQueries(newCache);
    localStorage.setItem('blastCachedQueries', JSON.stringify(newCache));
    if (selectedCachedQuery === name) {
      setSelectedCachedQuery('');
    }
  };

  const loadCachedQuery = (name: string) => {
    if (cachedQueries[name]) {
      setTextareaValue(cachedQueries[name]);
      setSelectedCachedQuery(name);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().match(/\.(fasta|fa|fas|faa)$/)) {
      setWarning('Please upload a FASTA file (.fasta, .fa, .fas, .faa)');
      return;
    }

    setSelectedCachedQuery('');
    setWarning('');

    try {
      const text = await file.text();
      const lines = text.trim().split(/\r?\n/);

      if (!lines[0].startsWith(">")) {
        setWarning("Invalid FASTA format. The file must start with a header line (>)");
        setUploadedFile(null);
        return;
      }

      const headerCount = lines.filter(line => line.startsWith(">")).length;
      if (headerCount !== 1) {
        setWarning("Please upload a FASTA file containing only one sequence.");
        setUploadedFile(null);
        return;
      }

      setUploadedFile(file);
      setTextareaValue(text);
      setWarning('');

      const fileName = file.name.replace(/\.(fasta|fa|fas|faa)$/i, '');
      saveQueryToCache(fileName, text);
      setSelectedCachedQuery(fileName);

    } catch (error) {
      console.error('Error reading file:', error);
      setWarning('Error reading file. Please try again.');
      setUploadedFile(null);
    }
  };

  const handleSaveQuery = () => {
    if (!textareaValue.trim()) {
      setWarning('No sequence to save');
      return;
    }

    const name = prompt('Enter a name for this query:');
    if (name && name.trim()) {
      saveQueryToCache(name.trim(), textareaValue);
      setSelectedCachedQuery(name.trim());
      setWarning('');
    }
  };

  const parseBlastOutput = (output: string): BlastHit[] => {
    const lines = output.split('\n');
    const hits: BlastHit[] = [];
    for (const line of lines) {
      if (!line.trim() || line.startsWith('#')) continue;
      const fields = line.split('\t');
      if (fields.length >= 12) {
        const hit: BlastHit = {
          query: fields[0],
          subject: fields[1],
          identity: parseFloat(fields[2]),
          alignmentLength: parseInt(fields[3]),
          mismatches: parseInt(fields[4]),
          gapOpens: parseInt(fields[5]),
          queryStart: parseInt(fields[6]),
          queryEnd: parseInt(fields[7]),
          subjectStart: parseInt(fields[8]),
          subjectEnd: parseInt(fields[9]),
          eValue: fields[10],
          bitScore: parseFloat(fields[11])
        };
        hits.push(hit);
      }
    }
    return hits;
  };

  function formatSubject(subject: string): string {
    const parts = subject.split("|");
    parts.shift(); // เอาตัวแรกออก
    return parts.join(" "); // รวมกลับเป็น string (จะใช้ space หรือ "" ก็ได้)
  }

  const handleSubmit = async () => {

    if (!textareaValue && !uploadedFile) {
      setWarning("Please paste or upload a sequence first.");
      return;
    }

    const text = textareaValue.trim();
    const lines = text.split(/\r?\n/);

    if (!lines[0]?.startsWith(">")) {
      setWarning("Invalid FASTA format. Sequence must start with a header line (>)");
      return;
    }

    const headerCount = lines.filter(line => line.startsWith(">")).length;
    if (headerCount > 1) {
      setWarning("Please provide only one FASTA sequence for BLAST search.");
      return;
    }

    setWarning('');
    setBlastResult(null);
    setParsedResults([]);
    setLoading(true);

    try {
      const response = await fetch("/api/run-blast", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: textareaValue,
          target: searchBy,
          blast_type: searchBy.endsWith('.fna') ? 'blastn' : 'blastp',
          output_format: 'tabular'
        }),
      });

      if (response.ok) {
        const result = await response.text();
        setBlastResult(result);
        setParsedResults(parseBlastOutput(result));
      } else {
        const error = await response.text();
        setBlastResult(`Error: ${error}`);
        setParsedResults([]);
      }
    } catch (error) {
      console.error("Blast error:", error);
      setBlastResult(`Unexpected error occurred`);
      setParsedResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Head>
        <title>BLAST Search - ASFV-EpiBase</title>
        <meta name="description" content="Search protein sequences using BLAST alignment tool in ASFV-EpiBase" />
      </Head>
      <ProteinPageLayout>
      <div className="flex flex-col gap-4 p-4 bg-gray-50 rounded-lg shadow">
        {/* Page Title */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">BLAST Search</h1>
          <p className="text-gray-600">Search protein sequences using BLAST alignment tool</p>
        </div>
        
        <h2 className="text-2xl font-bold mb-4">Search by BLAST</h2>

        {/* Target Selector */}
        <div className="flex flex-col sm:flex-row gap-2">
          <label htmlFor="searchBy" className="text-sm font-medium text-gray-700">Target File</label>
          <select
            id="searchBy"
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            className="border rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
          >
            {/* <option value="GCF_003047695.1_ASM304769v1_genomic.fna">BLAST in the genome</option> */}
            {/* <option value="GCF_003047695.1_ASM304769v1_translated_cds.faa">BLAST in CDS sequences</option> */}
            <option value="GCF_003047695.1_ASM304769v1_protein.faa">BLAST in protein sequences</option>
          </select>
        </div>

        {/* Query Input Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Query Input</h3>
          
          {/* File Upload */}
          <div className="flex flex-col gap-2">
            <label htmlFor="fileUpload" className="text-sm font-medium text-gray-700">
              Upload FASTA File
            </label>
            <div className="flex items-center gap-2">
              <input
                id="fileUpload"
                type="file"
                accept=".fasta,.fa,.fas,.faa"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {uploadedFile && (
                <span className="text-sm text-green-600">✓ {uploadedFile.name}</span>
              )}
            </div>
          </div>

          {/* Cached Queries */}
          {Object.keys(cachedQueries).length > 0 && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Cached Queries</label>
              <div className="flex flex-wrap gap-2">
                {Object.keys(cachedQueries).map((name) => (
                  <div key={name} className="flex items-center gap-1">
                    <button
                      onClick={() => loadCachedQuery(name)}
                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                        selectedCachedQuery === name
                          ? 'bg-blue-100 text-blue-800 border-blue-300'
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {name}
                    </button>
                    <button
                      onClick={() => deleteQueryFromCache(name)}
                      className="text-red-500 hover:text-red-700 text-xs"
                      title="Delete cached query"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Textarea */}
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <label htmlFor="inputText" className="text-sm font-medium text-gray-700">
                Enter or Paste a Protein Sequence
              </label>
              <button
                onClick={handleSaveQuery}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Save Query
              </button>
            </div>
            <textarea
              id="inputText"
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
              className="border rounded-md px-3 py-2 w-full h-40 focus:ring-2 focus:ring-blue-500 font-mono text-sm resize-y"
              placeholder="Enter your sequence or query here, or upload a FASTA file above"
            />
            {textareaValue && (
              <div className="text-xs text-gray-500">
                Characters: {textareaValue.length} | Lines: {textareaValue.split('\n').length}
              </div>
            )}
            {warning && <p className="text-red-500 text-sm">{warning}</p>}
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors disabled:opacity-50"
          >
            {loading ? "Running..." : "Submit"}
          </button>
        </div>

        {/* Results Table */}
        {parsedResults.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">BLAST Results ({parsedResults.length} hits)</h3>
            <div className="overflow-x-auto bg-white border rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Query ID
                    </th> */}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Accession
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      % Identity
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Alignment Length
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mismatches
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gap Opens
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Query Range
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subject Range
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      E-value
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bit Score
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {parsedResults.map((hit, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {/* <td className="px-4 py-3 text-sm text-gray-900 font-mono max-w-xs truncate" title={hit.query}>
                        {hit.query}
                      </td> */}
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono max-w-xs truncate" title={hit.subject}>
                      <Link href={`/protein_web/${hit.subject.split("|")[0]}`}>{hit.subject.split("|")[0]}</Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono max-w-xs truncate" title={hit.subject}>
                      <Link href={`/protein_web/${hit.subject.split("|")[0]}`}>
                          {formatSubject(hit.subject)}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          hit.identity >= 90 ? 'bg-green-100 text-green-800' :
                          hit.identity >= 70 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {hit.identity.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {hit.alignmentLength}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {hit.mismatches}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {hit.gapOpens}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">
                        {hit.queryStart}-{hit.queryEnd}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">
                        {hit.subjectStart}-{hit.subjectEnd}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">
                        {hit.eValue}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {hit.bitScore.toFixed(1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Raw Results (Fallback) */}
        {blastResult && parsedResults.length === 0 && !loading && (
          <div className="mt-6 p-4 bg-white border rounded-md">
            <h3 className="text-lg font-semibold mb-2">BLAST Result (Raw Output)</h3>
            <div className="bg-gray-50 p-3 rounded border overflow-auto max-h-96">
              <pre className="text-sm font-mono whitespace-pre-wrap">{blastResult}</pre>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {blastResult && parsedResults.length === 0 && !blastResult.includes('Error') && !loading && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-yellow-800">No BLAST hits found or results could not be parsed into table format.</p>
          </div>
        )}
      </div>
      </ProteinPageLayout>
    </>
  );
};

export default SearchBlastPage;